import SafeEmailPreview from '../common/SafeEmailPreview';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBase, getAuthHeaders } from '../../utils/api';

const ParsingReviewQueue = () => {
  const { user } = useAuth();
  const { reviewItemId } = useParams();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [deepLinkLoaded, setDeepLinkLoaded] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    status: 'pending',
    priority: '',
    document_type: '',
  });

  // Pagination
  const [pagination, setPagination] = useState({
    offset: 0,
    limit: 20,
    total: 0
  });

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const params = new URLSearchParams({
        limit: pagination.limit,
        offset: pagination.offset,
        sort_by: 'created_at',
        sort_order: 'desc'
      });

      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.document_type) params.append('document_type', filters.document_type);

      const response = await axios.get(
        `${apiBase}/admin/parsing-qa/queue?${params.toString()}`,
        { headers }
      );

      setItems(response.data.items || []);
      setPagination(prev => ({ ...prev, total: response.data.total }));
    } catch (err) {
      console.error('Error loading queue:', err);
      setError('Failed to load review queue: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit, pagination.offset]);

  const loadStats = useCallback(async () => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.get(`${apiBase}/admin/parsing-qa/queue/stats`, { headers });
      setStats(response.data.stats);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  }, []);

  // Load a specific item by ID (for deep links from Slack/email)
  const loadItemById = useCallback(async (itemId) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.get(`${apiBase}/admin/parsing-qa/queue/${itemId}`, { headers });
      if (response.data.item) {
        setSelectedItem(response.data.item);
        // Clear status filter to show all items so the selected one appears in the list
        setFilters(prev => ({ ...prev, status: '' }));
      }
    } catch (err) {
      console.error('Error loading item by ID:', err);
      setError('Failed to load the requested review item: ' + (err.response?.data?.detail || err.message));
    }
  }, []);

  useEffect(() => {
    loadQueue();
    loadStats();
  }, [loadQueue, loadStats]);

  // Handle deep link to specific review item
  useEffect(() => {
    if (reviewItemId && !deepLinkLoaded) {
      setDeepLinkLoaded(true);
      loadItemById(reviewItemId);
    }
  }, [reviewItemId, deepLinkLoaded, loadItemById]);

  const handleAssign = async (itemId) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.post(
        `${apiBase}/admin/parsing-qa/queue/${itemId}/assign`,
        {},
        { headers }
      );
      setSelectedItem(response.data.item);
      loadQueue();
      setSuccessMessage('Item assigned to you');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to assign item: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleComplete = async (itemId, status, correctedData, notes) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const corrections = [];
      if (correctedData && selectedItem) {
        // Detect what changed
        const original = selectedItem.ai_parsed_data || {};
        for (const [key, value] of Object.entries(correctedData)) {
          if (JSON.stringify(original[key]) !== JSON.stringify(value)) {
            corrections.push({
              field: key,
              old: original[key],
              new: value
            });
          }
        }
      }

      await axios.post(
        `${apiBase}/admin/parsing-qa/queue/${itemId}/complete`,
        {
          status,
          corrected_data: correctedData,
          corrections_applied: corrections,
          review_notes: notes
        },
        { headers }
      );

      setSelectedItem(null);
      loadQueue();
      loadStats();
      setSuccessMessage(`Item ${status === 'approved' ? 'approved' : 'rejected'} successfully`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to complete review: ' + (err.response?.data?.detail || err.message));
    }
  };

  const priorityColors = {
    urgent: 'bg-red-100 text-red-800 border-red-200',
    high: 'bg-orange-100 text-orange-800 border-orange-200',
    medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    low: 'bg-green-100 text-green-800 border-green-200'
  };

  const statusColors = {
    pending: 'bg-gray-100 text-gray-800',
    in_progress: 'bg-blue-100 text-blue-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800'
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const formatAge = (dateStr) => {
    if (!dateStr) return '-';
    const created = new Date(dateStr);
    const now = new Date();
    const diffMs = now - created;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffHours > 24) {
      return `${Math.floor(diffHours / 24)}d ${diffHours % 24}h`;
    }
    if (diffHours > 0) {
      return `${diffHours}h ${diffMins}m`;
    }
    return `${diffMins}m`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Parsing Review Queue</h1>
            <p className="text-sm sm:text-base text-gray-600">Review and correct parsing exceptions</p>
          </div>
          <button
            onClick={() => { loadQueue(); loadStats(); }}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm sm:text-base whitespace-nowrap self-start sm:self-auto"
          >
            Refresh
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4">
            <div className="bg-gray-50 rounded-lg p-2 sm:p-3">
              <div className="text-xs sm:text-sm text-gray-500">Total</div>
              <div className="text-lg sm:text-2xl font-semibold">{stats.total}</div>
            </div>
            <div className="bg-yellow-50 rounded-lg p-2 sm:p-3">
              <div className="text-xs sm:text-sm text-yellow-600">Pending</div>
              <div className="text-lg sm:text-2xl font-semibold text-yellow-700">
                {stats.by_status?.pending || 0}
              </div>
            </div>
            <div className="bg-blue-50 rounded-lg p-2 sm:p-3">
              <div className="text-xs sm:text-sm text-blue-600">In Progress</div>
              <div className="text-lg sm:text-2xl font-semibold text-blue-700">
                {stats.by_status?.in_progress || 0}
              </div>
            </div>
            <div className="bg-red-50 rounded-lg p-2 sm:p-3">
              <div className="text-xs sm:text-sm text-red-600">Urgent</div>
              <div className="text-lg sm:text-2xl font-semibold text-red-700">
                {stats.by_priority?.urgent || 0}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-2 sm:p-3 col-span-2 sm:col-span-1">
              <div className="text-xs sm:text-sm text-gray-500">Oldest Item</div>
              <div className="text-base sm:text-lg font-semibold">
                {stats.oldest_age_hours ? `${stats.oldest_age_hours.toFixed(1)}h` : '-'}
              </div>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
          <button onClick={() => setError('')} className="float-right">×</button>
        </div>
      )}

      {successMessage && (
        <div className="mx-6 mt-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md">
          {successMessage}
        </div>
      )}

      <div className="flex flex-col lg:flex-row">
        {/* Queue List */}
        <div className={`${selectedItem ? 'hidden lg:block lg:w-1/2' : 'w-full'} p-4 sm:p-6`}>
          {/* Filters */}
          <div className="mb-4 flex flex-wrap gap-2 sm:gap-4">
            <select
              value={filters.status}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, status: e.target.value }));
                setPagination(prev => ({ ...prev, offset: 0 }));
              }}
              className="px-2 sm:px-3 py-2 border border-gray-300 rounded-md text-xs sm:text-sm flex-1 min-w-[100px] max-w-[150px]"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>

            <select
              value={filters.priority}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, priority: e.target.value }));
                setPagination(prev => ({ ...prev, offset: 0 }));
              }}
              className="px-2 sm:px-3 py-2 border border-gray-300 rounded-md text-xs sm:text-sm flex-1 min-w-[100px] max-w-[150px]"
            >
              <option value="">All Priority</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>

            <select
              value={filters.document_type}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, document_type: e.target.value }));
                setPagination(prev => ({ ...prev, offset: 0 }));
              }}
              className="px-2 sm:px-3 py-2 border border-gray-300 rounded-md text-xs sm:text-sm flex-1 min-w-[100px] max-w-[150px]"
            >
              <option value="">All Types</option>
              <option value="flight">Flight</option>
              <option value="hotel">Hotel</option>
              <option value="car_rental">Car Rental</option>
              <option value="train">Train</option>
            </select>
          </div>

          {/* Queue Items */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No items in the queue matching your filters
            </div>
          ) : (
            <div className="space-y-3">
              {items.map(item => (
                <div
                  key={item.id}
                  onClick={() => setSelectedItem(item)}
                  className={`bg-white rounded-lg border p-4 cursor-pointer hover:shadow-md transition-shadow ${
                    selectedItem?.id === item.id ? 'ring-2 ring-blue-500' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded border ${priorityColors[item.priority]}`}>
                          {item.priority.toUpperCase()}
                        </span>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${statusColors[item.status]}`}>
                          {item.status.replace('_', ' ')}
                        </span>
                        <span className="text-xs text-gray-500">
                          {item.document_type.replace('_', ' ')}
                        </span>
                      </div>

                      <div className="font-medium text-gray-900">{item.organization_name}</div>
                      <div className="text-sm text-gray-500">{item.email_subject || 'No subject'}</div>

                      <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                        <span>From: {item.email_from || 'Unknown'}</span>
                        {item.carrier_code && <span>Carrier: {item.carrier_code}</span>}
                        <span>Age: {formatAge(item.created_at)}</span>
                      </div>

                      {item.validation_errors?.length > 0 && (
                        <div className="mt-2 text-xs text-red-600">
                          Issues: {item.validation_errors.slice(0, 2).join(', ')}
                          {item.validation_errors.length > 2 && ` +${item.validation_errors.length - 2} more`}
                        </div>
                      )}
                    </div>

                    <div className="text-right">
                      {item.travel_date && (
                        <div className="text-sm">
                          <span className="text-gray-500">Travel:</span>{' '}
                          <span className="font-medium">{item.travel_date}</span>
                        </div>
                      )}
                      {item.assigned_to_user_email && (
                        <div className="text-xs text-blue-600 mt-1">
                          Assigned: {item.assigned_to_user_email}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination.total > pagination.limit && (
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-2">
              <div className="text-xs sm:text-sm text-gray-500">
                Showing {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))}
                  disabled={pagination.offset === 0}
                  className="px-3 py-1 bg-gray-100 rounded text-xs sm:text-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
                  disabled={pagination.offset + pagination.limit >= pagination.total}
                  className="px-3 py-1 bg-gray-100 rounded text-xs sm:text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Detail Panel */}
        {selectedItem && (
          <ReviewDetailPanel
            item={selectedItem}
            onClose={() => setSelectedItem(null)}
            onAssign={() => handleAssign(selectedItem.id)}
            onComplete={handleComplete}
            currentUserId={user?.id}
          />
        )}
      </div>
    </div>
  );
};

// Detail Panel Component
const ReviewDetailPanel = ({ item, onClose, onAssign, onComplete, currentUserId }) => {
  const [editedData, setEditedData] = useState(item.ai_parsed_data || {});
  const [notes, setNotes] = useState('');
  const [activeTab, setActiveTab] = useState('parsed');

  const isAssignedToMe = item.assigned_to_user_id === currentUserId;
  const canReview = item.status === 'in_progress' && isAssignedToMe;

  const handleFieldChange = (field, value) => {
    setEditedData(prev => ({ ...prev, [field]: value }));
  };

  const renderParsedData = () => {
    const fields = [
      { key: 'traveler_name', label: 'Traveler Name' },
      { key: 'confirmation_number', label: 'Confirmation #' },
      { key: 'airline', label: 'Airline' },
      { key: 'airline_code', label: 'Airline Code' },
    ];

    // Add leg-specific fields if present
    const legs = editedData.legs || [];

    return (
      <div className="space-y-4">
        {fields.map(({ key, label }) => (
          <div key={key}>
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            <input
              type="text"
              value={editedData[key] || ''}
              onChange={(e) => handleFieldChange(key, e.target.value)}
              disabled={!canReview}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm disabled:bg-gray-50"
            />
          </div>
        ))}

        {legs.length > 0 && (
          <div className="mt-6">
            <h4 className="font-medium text-gray-900 mb-3">Flight Legs</h4>
            {legs.map((leg, idx) => (
              <div key={idx} className="border rounded-lg p-4 mb-4 bg-gray-50">
                <div className="font-medium text-sm text-gray-700 mb-2">Leg {idx + 1}</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500">Flight #</label>
                    <input
                      type="text"
                      value={leg.flight_number || ''}
                      onChange={(e) => {
                        const newLegs = [...legs];
                        newLegs[idx] = { ...leg, flight_number: e.target.value };
                        handleFieldChange('legs', newLegs);
                      }}
                      disabled={!canReview}
                      className="w-full px-2 py-1 border rounded text-sm disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500">Departure</label>
                    <input
                      type="text"
                      value={leg.departure_airport || ''}
                      onChange={(e) => {
                        const newLegs = [...legs];
                        newLegs[idx] = { ...leg, departure_airport: e.target.value };
                        handleFieldChange('legs', newLegs);
                      }}
                      disabled={!canReview}
                      className="w-full px-2 py-1 border rounded text-sm disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500">Arrival</label>
                    <input
                      type="text"
                      value={leg.arrival_airport || ''}
                      onChange={(e) => {
                        const newLegs = [...legs];
                        newLegs[idx] = { ...leg, arrival_airport: e.target.value };
                        handleFieldChange('legs', newLegs);
                      }}
                      disabled={!canReview}
                      className="w-full px-2 py-1 border rounded text-sm disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500">Departure Date</label>
                    <input
                      type="datetime-local"
                      value={leg.departure_date?.slice(0, 16) || ''}
                      onChange={(e) => {
                        const newLegs = [...legs];
                        newLegs[idx] = { ...leg, departure_date: e.target.value };
                        handleFieldChange('legs', newLegs);
                      }}
                      disabled={!canReview}
                      className="w-full px-2 py-1 border rounded text-sm disabled:bg-gray-100"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full lg:w-1/2 border-l-0 lg:border-l border-gray-200 bg-white overflow-auto" style={{ height: 'calc(100vh - 200px)', minHeight: '400px' }}>
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 z-10">
        <div className="flex items-center justify-between">
          <h3 className="text-base sm:text-lg font-semibold">Review Item</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Action Buttons */}
        <div className="mt-4 flex flex-wrap gap-2">
          {item.status === 'pending' && (
            <button
              onClick={onAssign}
              className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-md text-xs sm:text-sm hover:bg-blue-700"
            >
              Assign to Me
            </button>
          )}
          {canReview && (
            <>
              <button
                onClick={() => onComplete(item.id, 'approved', editedData, notes)}
                className="px-3 sm:px-4 py-2 bg-green-600 text-white rounded-md text-xs sm:text-sm hover:bg-green-700"
              >
                Approve
              </button>
              <button
                onClick={() => onComplete(item.id, 'rejected', null, notes)}
                className="px-3 sm:px-4 py-2 bg-red-600 text-white rounded-md text-xs sm:text-sm hover:bg-red-700"
              >
                Reject
              </button>
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4 sm:p-6">
        {/* Meta Info */}
        <div className="mb-6 p-3 sm:p-4 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 text-xs sm:text-sm">
            <div>
              <span className="text-gray-500">Organization:</span>{' '}
              <span className="font-medium">{item.organization_name}</span>
            </div>
            <div>
              <span className="text-gray-500">Type:</span>{' '}
              <span className="font-medium">{item.document_type}</span>
            </div>
            <div className="truncate">
              <span className="text-gray-500">Sender:</span>{' '}
              <span className="font-medium">{item.email_from}</span>
            </div>
            <div>
              <span className="text-gray-500">Carrier:</span>{' '}
              <span className="font-medium">{item.carrier_code || '-'}</span>
            </div>
            <div>
              <span className="text-gray-500">Sender Type:</span>{' '}
              <span className="font-medium">{item.sender_type?.replace('_', ' ')}</span>
            </div>
            <div>
              <span className="text-gray-500">Confidence:</span>{' '}
              <span className="font-medium">
                {item.ai_confidence_score ? `${(item.ai_confidence_score * 100).toFixed(0)}%` : '-'}
              </span>
            </div>
          </div>
        </div>

        {/* Issues */}
        {(item.validation_errors?.length > 0 || item.missing_fields?.length > 0) && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <h4 className="font-medium text-red-800 mb-2">Issues Detected</h4>
            <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
              {item.validation_errors?.map((err, idx) => (
                <li key={`err-${idx}`}>{err}</li>
              ))}
              {item.missing_fields?.map((field, idx) => (
                <li key={`missing-${idx}`}>Missing: {field}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-4">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('parsed')}
              className={`pb-2 text-sm font-medium ${
                activeTab === 'parsed'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Parsed Data
            </button>
            <button
              onClick={() => setActiveTab('original')}
              className={`pb-2 text-sm font-medium ${
                activeTab === 'original'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Original Email
            </button>
            <button
              onClick={() => setActiveTab('comparison')}
              className={`pb-2 text-sm font-medium ${
                activeTab === 'comparison'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Comparison
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'parsed' && renderParsedData()}

        {activeTab === 'original' && (
          <div>
            {item.original_email_html ? (
              <SafeEmailPreview html={item.original_email_html} />
            ) : (
              <div className="text-center py-8 text-gray-500">
                Original email not available
              </div>
            )}
          </div>
        )}

        {activeTab === 'comparison' && (
          <div>
            {item.awardwallet_parsed_data ? (
              <div className="space-y-4">
                <h4 className="font-medium">AI vs AwardWallet Comparison</h4>
                {Object.entries(item.mismatched_fields || {}).map(([field, values]) => (
                  <div key={field} className="border rounded-lg p-3 bg-yellow-50">
                    <div className="font-medium text-sm">{field}</div>
                    <div className="grid grid-cols-2 gap-4 mt-2 text-sm">
                      <div>
                        <span className="text-gray-500">AI:</span> {JSON.stringify(values.ai)}
                      </div>
                      <div>
                        <span className="text-gray-500">AwardWallet:</span> {JSON.stringify(values.aw)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No AwardWallet data available for comparison
              </div>
            )}
          </div>
        )}

        {/* Notes */}
        {canReview && (
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Review Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this review..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ParsingReviewQueue;
